import { israelDateTime, israelDay, TASK_KIND_LABELS, type TaskKind, formatDateTime } from '@takumi/shared';
import { useMutation } from '@tanstack/react-query';
import { CalendarClock, Check } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Select, Textarea } from '@/components/ui/form';
import { Dialog, DialogContent } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useCompleteTask, useInvalidateCrm, useUserOptions } from '@/lib/queries';
import type { Contact, Task } from '@/lib/types';
import { defaultSchedule, FollowupScheduler, type ScheduleValue } from './FollowupScheduler';

/** Completing a follow-up always asks for the next one — presets first. */
export function CompleteTaskDialog({
  task,
  open,
  onOpenChange,
}: {
  task: Pick<Task, 'id' | 'title' | 'kind' | 'dueAt' | 'organizationId'> & { organizationName?: string | null };
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [outcome, setOutcome] = useState('');
  const [schedule, setSchedule] = useState<ScheduleValue>(defaultSchedule);
  const complete = useCompleteTask();

  useEffect(() => {
    if (open) {
      setOutcome('');
      setSchedule(task.organizationId ? defaultSchedule() : { preset: 'none', dueAt: null });
    }
  }, [open, task.organizationId]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await complete.mutateAsync({
      id: task.id,
      organizationId: task.organizationId,
      outcome: outcome.trim() || null,
      next: schedule.dueAt ? { dueAt: schedule.dueAt.toISOString() } : null,
    });
    toast.success(schedule.dueAt ? 'בוצע · הפולואפ הבא נקבע' : 'בוצע');
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={`${TASK_KIND_LABELS[task.kind]} בוצע`} description={[task.organizationName, task.title].filter(Boolean).join(' · ')}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Field label="מה היה?">
            {(id) => <Textarea id={id} autoFocus rows={3} value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="לדוגמה: דיברנו, מחכים לטעימה של הצוות" />}
          </Field>
          {task.organizationId && (
            <div className="rounded-card border border-matcha-200 bg-matcha-50/60 p-4">
              <div className="mb-3 flex items-center gap-2 font-semibold text-matcha-900">
                <CalendarClock className="size-4" /> הפולואפ הבא
              </div>
              <FollowupScheduler value={schedule} onChange={setSchedule} allowNone />
            </div>
          )}
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              ביטול
            </Button>
            <Button type="submit" loading={complete.isPending}>
              <Check /> סימון כבוצע
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Create a follow-up, task or meeting. */
export function NewTaskDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  contacts = [],
  defaultKind = 'followup',
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  organizationId?: string | null;
  organizationName?: string;
  contacts?: Pick<Contact, 'id' | 'fullName' | 'isPrimary'>[];
  defaultKind?: TaskKind;
}) {
  const [kind, setKind] = useState<TaskKind>(defaultKind);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [schedule, setSchedule] = useState<ScheduleValue>(defaultSchedule);
  const [day, setDay] = useState(israelDay());
  const [start, setStart] = useState('10:00');
  const [end, setEnd] = useState('11:00');
  const [location, setLocation] = useState('');
  const [contactId, setContactId] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const users = useUserOptions();
  const invalidate = useInvalidateCrm();

  useEffect(() => {
    if (open) {
      setKind(defaultKind);
      setTitle(defaultKind === 'meeting' ? 'פגישה' : defaultKind === 'followup' ? 'פולואפ' : '');
      setDescription('');
      setSchedule(defaultSchedule());
      setDay(israelDay());
      setLocation('');
      setContactId(contacts.find((c) => c.isPrimary)?.id ?? '');
      setAssigneeId('');
    }
  }, [open, defaultKind, contacts]);

  const create = useMutation({
    mutationFn: (body: unknown) => api.post<Task>('/tasks', body),
    onSuccess: (t) => {
      invalidate(organizationId);
      toast.success(`${TASK_KIND_LABELS[t.kind]} נקבע ל-${formatDateTime(t.dueAt)}`);
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const toDate = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return israelDateTime(day, h ?? 0, m ?? 0);
    };
    const isMeeting = kind === 'meeting';
    const dueAt = isMeeting || kind === 'task' ? toDate(start) : schedule.dueAt;
    if (!dueAt) {
      toast.error('יש לבחור מועד');
      return;
    }
    create.mutate({
      kind,
      organizationId: organizationId ?? null,
      contactId: contactId || null,
      assigneeId: assigneeId || null,
      title: title.trim(),
      description: description.trim() || null,
      dueAt: dueAt.toISOString(),
      endsAt: isMeeting ? toDate(end).toISOString() : null,
      location: isMeeting ? location.trim() || null : null,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="פולואפ / משימה / פגישה" description={organizationName} wide>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex gap-2">
            {(['followup', 'meeting', 'task'] as TaskKind[]).map((k) => (
              <Button key={k} variant={kind === k ? 'primary' : 'secondary'} size="sm" onClick={() => setKind(k)} aria-pressed={kind === k}>
                {TASK_KIND_LABELS[k]}
              </Button>
            ))}
          </div>
          <Field label="כותרת" required>
            {(id) => <Input id={id} required value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />}
          </Field>
          {kind === 'followup' ? (
            <FollowupScheduler value={schedule} onChange={setSchedule} />
          ) : (
            <div className="flex flex-wrap gap-3">
              <Field label="תאריך">{(id) => <Input id={id} type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-44" />}</Field>
              <Field label={kind === 'meeting' ? 'התחלה' : 'שעה'}>{(id) => <Input id={id} type="time" step={900} value={start} onChange={(e) => setStart(e.target.value)} className="w-28" />}</Field>
              {kind === 'meeting' && (
                <Field label="סיום">{(id) => <Input id={id} type="time" step={900} value={end} onChange={(e) => setEnd(e.target.value)} className="w-28" />}</Field>
              )}
            </div>
          )}
          {kind === 'meeting' && (
            <Field label="מיקום">{(id) => <Input id={id} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="כתובת / זום / טלפון" />}</Field>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {contacts.length > 0 && (
              <Field label="איש קשר">
                {(id) => (
                  <Select id={id} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                    <option value="">—</option>
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>{c.fullName}</option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <Field label="אחראי" hint="ברירת מחדל: בעל הלקוח">
              {(id) => (
                <Select id={id} value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                  <option value="">ברירת מחדל</option>
                  {users.data?.map((u) => (
                    <option key={u.id} value={u.id}>{u.fullName}</option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Field label="פרטים">{(id) => <Textarea id={id} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={create.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
